import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import {
  ExpenseClaim,
  ExpenseClaimCreateCommand,
  ExpenseClaimJournalPreview,
  ExpenseClaimSearchCommand,
  ExpenseClaimSummary,
  ExpenseClaimUpdate,
} from '../models/expense-claim/expense-claim';
import { ExpenseReceiptOwner, ExpenseReceiptUpdate } from '../models/expense-receipt/expense-receipt';
import { PagedResult } from './bank-account.proxy.service';

@Injectable({ providedIn: 'root' })
export class ExpenseClaimProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/expense-claim';

  public search(
    command: ExpenseClaimSearchCommand,
    pageNumber: number,
    pageSize: number,
    sortField: string | null,
    sortOrder: number,
  ): Observable<ApiResponse<PagedResult<ExpenseClaim>>> {
    let params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize).set('sortOrder', sortOrder);
    if (sortField) params = params.set('sortField', sortField);
    return this.httpClient.post<ApiResponse<PagedResult<ExpenseClaim>>>(`${this.baseUrl}/search`, command, { params });
  }

  public getSummary(): Observable<ApiResponse<ExpenseClaimSummary>> {
    return this.httpClient.get<ApiResponse<ExpenseClaimSummary>>(`${this.baseUrl}/summary`);
  }

  /** The funds (as custodian) and trips (as employee or TripAdmin) the caller can open claims for. */
  public getOwners(): Observable<ApiResponse<ExpenseReceiptOwner[]>> {
    return this.httpClient.get<ApiResponse<ExpenseReceiptOwner[]>>(`${this.baseUrl}/owners`);
  }

  public getById(id: string): Observable<ApiResponse<ExpenseClaim>> {
    return this.httpClient.get<ApiResponse<ExpenseClaim>>(`${this.baseUrl}/${id}`);
  }

  public create(command: ExpenseClaimCreateCommand): Observable<ApiResponse<ExpenseClaim>> {
    return this.httpClient.post<ApiResponse<ExpenseClaim>>(this.baseUrl, command);
  }

  public update(id: string, payload: ExpenseClaimUpdate): Observable<ApiResponse<ExpenseClaim>> {
    return this.httpClient.put<ApiResponse<ExpenseClaim>>(`${this.baseUrl}/${id}`, payload);
  }

  public addReceipts(id: string, receiptIds: string[]): Observable<ApiResponse<ExpenseClaim>> {
    return this.httpClient.post<ApiResponse<ExpenseClaim>>(`${this.baseUrl}/${id}/receipts`, { receiptIds });
  }

  /** Back to the pool: from a draft, or by an approver while in approval. */
  public removeReceipt(id: string, receiptId: string): Observable<ApiResponse<ExpenseClaim>> {
    return this.httpClient.delete<ApiResponse<ExpenseClaim>>(`${this.baseUrl}/${id}/receipts/${receiptId}`);
  }

  /** An approver sends one receipt back to its capturer as Rejected. */
  public rejectReceipt(id: string, receiptId: string, comment: string): Observable<ApiResponse<ExpenseClaim>> {
    return this.httpClient.post<ApiResponse<ExpenseClaim>>(`${this.baseUrl}/${id}/receipts/${receiptId}/reject`, { comment });
  }

  public submit(id: string): Observable<ApiResponse<ExpenseClaim>> {
    return this.httpClient.post<ApiResponse<ExpenseClaim>>(`${this.baseUrl}/${id}/submit`, {});
  }

  public approve(id: string, comment: string | null): Observable<ApiResponse<ExpenseClaim>> {
    return this.httpClient.post<ApiResponse<ExpenseClaim>>(`${this.baseUrl}/${id}/approve`, { comment });
  }

  public reject(id: string, comment: string): Observable<ApiResponse<ExpenseClaim>> {
    return this.httpClient.post<ApiResponse<ExpenseClaim>>(`${this.baseUrl}/${id}/reject`, { comment });
  }

  /** Accounting's review of one receipt: concept, cost center, tax, CAI and GL account per line (#5798). */
  public reviewReceipt(id: string, receiptId: string, update: ExpenseReceiptUpdate): Observable<ApiResponse<ExpenseClaim>> {
    return this.httpClient.put<ApiResponse<ExpenseClaim>>(`${this.baseUrl}/${id}/receipts/${receiptId}`, update);
  }

  public getJournalPreview(id: string): Observable<ApiResponse<ExpenseClaimJournalPreview>> {
    return this.httpClient.get<ApiResponse<ExpenseClaimJournalPreview>>(`${this.baseUrl}/${id}/journal-preview`);
  }

  /** Posts the claim's single journal entry. `postingDate` is yyyy-MM-dd. */
  public post(id: string, postingDate: string): Observable<ApiResponse<ExpenseClaim>> {
    return this.httpClient.post<ApiResponse<ExpenseClaim>>(`${this.baseUrl}/${id}/post`, { postingDate });
  }

  public sendBack(id: string, comment: string): Observable<ApiResponse<ExpenseClaim>> {
    return this.httpClient.post<ApiResponse<ExpenseClaim>>(`${this.baseUrl}/${id}/send-back`, { comment });
  }

  public delete(id: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseUrl}/${id}`);
  }
}
